import React, { useRef, useState } from 'react';
import { Button, Modal, Steps } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import Contract from '../classes/contract';
import PackageForm from './forms/package-form';
import VehicleForm from './forms/vehicle-form';
import CustomerForm from './forms/customer-form';
import ContactForm from './forms/contact-form';
import ContractSummary from './contract-summary';
import ShippingForm from './forms/shipping-form';
import InstallerForm from './forms/installer/installer-form';
import Installer from '../classes/installer';
import PaymentForm from './forms/payment/payment-form';
import ReactToPrint from 'react-to-print';
import User from '../classes/user';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store';
import SicuriProtettiServices from 'js/data/sicuri-protetti-services';
import { Utils } from 'js/tools/utils';

const { confirm } = Modal;
const { Step } = Steps;

interface IProps {
    data: Contract;
    userInfo: User;
    productFun: (productId: number, product?: string, price?: string, professional?: boolean) => void;
    periodFun: (periodId: number, period: string, id: number) => void;
    optionsFun: (option: string, op: number) => void;
    vehicleFun: (formData?: any) => void;
    customerFun: (formData?: any) => void;
    contactFun: (formData?: any) => void;
    shippingFun: (formData: any) => void;
    installerFun: (installer: Installer) => void;
    paymentFun: (formData?: any) => void;
}

const Stepper = (props: IProps) => {
    const { t } = useTranslation();
    const componentRef = useRef(null);

    const [currentStep, setCurrentStep] = useState<number>(0);
    const [formValid, setFormValid] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>(undefined);
    const setLoading = useStore((state) => state.setLoading);

    const onSubmit = (isValid: boolean, values?: any) => {
        setFormValid(isValid);
        setFormData(values);
        //if(formData.installer !== undefined) if(formData.installer !== props.data.installer.installerId) props.installerFun(formData.installer, formData.province);
        if (!isValid) Utils.ShowMessage('warning', formData);
    };

    const next = async () => {
        if (currentStep === 0) {
            if (props.data.package.productId === -1) {
                Utils.ShowMessage('warning', t('errorMessages.selPack'));
                return;
            }
            if (props.data.package.periodId === -1) {
                Utils.ShowMessage('warning', t('errorMessages.selPeriod'));
                return;
            }
        } else if (currentStep === 1) {
            if (formValid) {
                props.vehicleFun(formData);
            } else {
                document.getElementById('vehicle-submit')!.click();
                return;
            }
        } else if (currentStep === 2) {
            if (formValid) {
                await props.customerFun(formData);
            } else {
                document.getElementById('customer-submit')!.click();
                return;
            }
        } else if (currentStep === 3) {
            if (formValid) {
                await props.contactFun(formData);
            } else {
                document.getElementById('contact-submit')!.click();
                return;
            }
        } else if (currentStep === 4) {
            if (document.getElementById('shipping-submit')) {
                if (formValid) {
                    props.shippingFun(formData);
                } else {
                    document.getElementById('shipping-submit')!.click();
                    return;
                }
            } else if (props.data.installer!.installerId === undefined) {
                Utils.ShowMessage('warning', t('errorMessages.selInst'));
                return;
            }
        } else if (currentStep === 5) {
            if (formValid) {
                await props.paymentFun(formData);
            } else {
                document.getElementById('payment-submit')!.click();
                return;
            }
        }
        setCurrentStep(currentStep + 1);
        setFormValid(false);
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
        setFormValid(false);
    };

    const complete = async () => {
        setLoading(true);
        const response = await SicuriProtettiServices.sendPractices(props.data, props.userInfo.uid!, 'SEP');
        setLoading(false);
        window.scroll(0, 0);
        if (response.error !== undefined) Utils.ShowMessage('error', response.error);
        else
            confirm({
                title: t('commonClass.success'),
                icon: <ExclamationCircleOutlined style={{ color: '#52c41a' }} />,
                content: (
                    <>
                        {t('commonClass.contractInserted')}
                        <strong>{response.contractId}</strong>
                    </>
                ),
                okText: t('commonClass.newContract'),
                okType: 'primary',
                cancelText: t('commonClass.viewContracts'),
                type: 'success',
                onOk() {
                    window.location.href = '/sep/add';
                },
                onCancel() {
                    window.location.href = '/sep/view';
                },
            });
    };

    const onChange = (current: number) => {
        if (currentStep >= current) setCurrentStep(current);
        else if (currentStep + 1 === current) next();
        else Utils.ShowMessage('warning', t('commonClass.compilePrevStep'));
    };

    const steps = [
        {
            title: t('commonClass.package'),
            content: (
                <PackageForm
                    data={props.data.package}
                    productFun={props.productFun}
                    periodFun={props.periodFun}
                    optionsFun={props.optionsFun}
                />
            ),
        },
        {
            title: t('vehicleClass.vehicle'),
            content: <VehicleForm data={props.data.vehicle} formIsValid={onSubmit} nextStep={next} />,
        },
        {
            title: t('commonClass.contractor'),
            content: <CustomerForm data={props.data.customer} formIsValid={onSubmit} nextStep={next} />,
        },
        {
            title: t('commonClass.contacts'),
            content: <ContactForm data={props.data.contact} formIsValid={onSubmit} nextStep={next} />,
        },
        {
            title: t('commonClass.installation'),
            content:
                props.data.package.professional === false ? (
                    <ShippingForm data={props.data.shipping} formIsValid={onSubmit} nextStep={next} />
                ) : (
                    <InstallerForm data={props.data.installer} formIsValid={onSubmit} installerFun={props.installerFun} nextStep={next} />
                ),
        },
        {
            title: t('commonClass.paymentData'),
            content: <PaymentForm data={props.data.payment} formIsValid={onSubmit} nextStep={next} />,
        },
        {
            title: t('commonClass.summary'),
            content: <ContractSummary data={props.data} translator={t} ref={componentRef} />,
        },
    ];
    const current = currentStep;
    return (
        <>
            <div className="stepper">
                <Steps current={current} onChange={onChange}>
                    {steps.map((item) => (
                        <Step key={item.title} title={item.title} />
                    ))}
                </Steps>
            </div>
            <div className="steps-content">{steps[current].content}</div>
            <div className="steps-action right">
                {current > 0 && (
                    <Button style={{ margin: '0 8px' }} onClick={() => prev()}>
                        {t('commonClass.prev')}
                    </Button>
                )}
                {current < steps.length - 1 && (
                    <Button type="primary" onClick={() => next()}>
                        {t('commonClass.next')}
                    </Button>
                )}
                {current === steps.length - 1 && (
                    <>
                        <Button type="primary" style={{ margin: '0 8px' }} onClick={() => complete()}>
                            {t('commonClass.complete')}
                        </Button>
                        <ReactToPrint
                            trigger={() => {
                                // NOTE: could just as easily return <SomeComponent />. Do NOT pass an `onClick` prop
                                // to the root node of the returned component as it will be overwritten.
                                return (
                                    <Button
                                        type="primary"
                                        className="print-button"
                                        icon={<FontAwesomeIcon icon={faPrint} title={t('commonClass.print')} />}
                                        title={t('commonClass.print')}
                                    ></Button>
                                );
                            }}
                            content={() => componentRef.current as unknown as Element}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default Stepper;
